/**
 * UserCell — shared owner / salesperson cell for DataTable columns.
 *
 * Renders the user's avatar (or a fallback silhouette) next to their
 * name in the same style used by the orders Salesperson column. Wraps
 * long names in `PopOutCell` so the table doesn't need a wide column
 * to accommodate edge cases.
 */

import { Avatar } from '@/components/ui/avatar';
import { PopOutCell } from './DataTable/PopOutCell';

interface UserLike {
  name: string;
  avatar_url: string | null;
}

interface UserCellProps {
  user: UserLike | null;
  /**
   * Wrap the cell in `PopOutCell` once the name exceeds this length.
   * Pass the column's design width as `popoutMinWidth`.
   */
  truncateAfter?: number;
  popoutMinWidth?: number;
}

export function UserCell({ user, truncateAfter, popoutMinWidth }: UserCellProps) {
  if (!user) return <span className="font-inter text-sm text-grey">-</span>;

  const avatar = (
    <Avatar size="lg" src={user.avatar_url ?? undefined} name={user.name} />
  );

  const collapsed = (
    <div className="flex items-center gap-3 min-w-0">
      {avatar}
      <span className="font-inter text-sm leading-[1.5] font-semibold text-off-black whitespace-nowrap overflow-hidden text-ellipsis">
        {user.name}
      </span>
    </div>
  );

  if (!truncateAfter || user.name.length <= truncateAfter) return collapsed;

  return (
    <PopOutCell
      minWidth={popoutMinWidth}
      collapsed={collapsed}
      expanded={
        <div className="flex items-center gap-3 min-w-0">
          {avatar}
          <span className="font-inter text-sm leading-[1.5] font-semibold text-off-black whitespace-nowrap">
            {user.name}
          </span>
        </div>
      }
    />
  );
}
